import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@repo/utils";

type Props = {
  children: ReactNode;
  className?: string;
  viewportClassName?: string;
  topFadeClassName?: string;
  bottomFadeClassName?: string;
};

type FadeState = { top: boolean; bottom: boolean };

const EDGE_THRESHOLD = 1;

export const ScrollFadeArea = ({
  children,
  className,
  viewportClassName,
  topFadeClassName,
  bottomFadeClassName,
}: Props) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<FadeState>({ top: false, bottom: false });

  const updateFade = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const { scrollTop, scrollHeight, clientHeight } = viewport;
    const top = scrollTop > EDGE_THRESHOLD;
    const bottom = scrollHeight - clientHeight - scrollTop > EDGE_THRESHOLD;

    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    updateFade();
    const observer = new ResizeObserver(updateFade);
    observer.observe(viewport);
    observer.observe(content);
    return () => observer.disconnect();
  }, [updateFade]);

  return (
    <div className={cn("relative flex min-h-0 flex-col", className)}>
      <div
        ref={viewportRef}
        onScroll={updateFade}
        className={cn(
          "isolate min-h-0 flex-1 overflow-y-auto overscroll-contain",
          "[scrollbar-color:var(--color-gray-300)_transparent] [scrollbar-width:thin]",
          viewportClassName
        )}
      >
        <div ref={contentRef}>{children}</div>
      </div>

      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-linear-to-b from-white via-white/70 to-transparent transition-opacity duration-200",
          fade.top ? "opacity-100" : "opacity-0",
          topFadeClassName
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-linear-to-t from-white via-white/70 to-transparent transition-opacity duration-200",
          fade.bottom ? "opacity-100" : "opacity-0",
          bottomFadeClassName
        )}
      />
    </div>
  );
};

ScrollFadeArea.displayName = "ScrollFadeArea";
